import TextField from '@mui/material/TextField';
import React from 'react';
import * as yup from 'yup';

import { BooleanFormFieldInput } from '@/components/molecules/BooleanFormFieldInput';
import { CNPJFormFieldInput } from '@/components/molecules/CNPJFormFieldInput';
import { CompositeFormFieldElement } from '@/components/molecules/CompositeFormFieldElement';
import { DateFormFieldElement } from '@/components/molecules/DateFormFieldElement';
import { EmbeddedFormFieldElement } from '@/components/molecules/EmbeddedFormFieldElement';
import { FileFormFieldElement } from '@/components/molecules/FileFormFieldElement';
import { FormFieldWrapper } from '@/components/molecules/FormFieldWrapper';
import { GenericSelect } from '@/components/molecules/GenericSelect';
import { ListFormFieldElement } from '@/components/molecules/ListFormFieldElement';
import { MultipleValuesFormFieldInput } from '@/components/molecules/MultipleValuesFormFieldInput';
import { NumericFormFieldInput } from '@/components/molecules/NumericFormFieldInput';
import { PhoneNumberFormFieldInput } from '@/components/molecules/PhoneNumberFormFieldInput';
import { useTranslator } from '@/hooks/useTranslator';
import { Field, FieldType } from '@/types/fields';
import { translateField } from '@/utility/translateField';

interface Props<T> {
  readonly field: Field<T>;
  // FIXME: should do something more "clever" about this
  readonly errors?: Record<string, yup.ValidationError>;
  readonly error?: yup.ValidationError;
  readonly defaultValue?: unknown;
}

export function FormFieldInput<T>(props: Props<T>): React.ReactElement {
  const { field: untranslatedField, error, errors, defaultValue = '' } = props;

  const tr = useTranslator();
  const field = React.useMemo(
    (): Field<T> => translateField(tr, untranslatedField),
    [tr, untranslatedField],
  );

  const { type, name, title } = field;

  switch (type) {
    case FieldType.phoneNumber: // Implement phone number validation
      return (
        <FormFieldWrapper
          name={String(name)}
          title={title}
          label={field.label}
          description={field.description}
          required={field.required}
          help={field.help}
          error={error}>
          <PhoneNumberFormFieldInput defaultValue={defaultValue} />
        </FormFieldWrapper>
      );

    case FieldType.cnpj:
      return (
        <FormFieldWrapper
          name={String(name)}
          title={title}
          label={field.label}
          description={field.description}
          required={field.required}
          help={field.help}
          error={error}>
          <CNPJFormFieldInput defaultValue={defaultValue} />
        </FormFieldWrapper>
      );

    case FieldType.email:
      return (
        <FormFieldWrapper
          name={String(name)}
          title={title}
          label={field.label}
          description={field.description}
          required={field.required}
          help={field.help}
          error={error}>
          <TextField defaultValue={defaultValue} />
        </FormFieldWrapper>
      );

    case FieldType.text:
      return (
        <FormFieldWrapper
          name={String(name)}
          title={title}
          label={field.label}
          description={field.description}
          required={field.required}
          help={field.help}
          error={error}>
          <TextField defaultValue={defaultValue} />
        </FormFieldWrapper>
      );

    case FieldType.numeric:
      return (
        <FormFieldWrapper
          name={String(name)}
          title={title}
          label={field.label}
          description={field.description}
          required={field.required}
          help={field.help}
          error={error}>
          <NumericFormFieldInput defaultValue={defaultValue} />
        </FormFieldWrapper>
      );

    case FieldType.paragraph:
      return (
        <FormFieldWrapper
          name={String(name)}
          title={title}
          label={field.label}
          description={field.description}
          required={field.required}
          help={field.help}
          error={error}>
          <TextField defaultValue={defaultValue} rows={5} multiline={true} />
        </FormFieldWrapper>
      );

    case FieldType.boolean:
      return (
        <FormFieldWrapper
          name={String(name)}
          title={title}
          label={''}
          required={field.required}
          help={field.help}
          error={error}>
          <BooleanFormFieldInput defaultValue={!!defaultValue} />
        </FormFieldWrapper>
      );

    case FieldType.multipleValues:
      return (
        <FormFieldWrapper
          name={String(name)}
          title={title}
          label=""
          required={field.required}
          help={field.help}
          error={error}>
          <MultipleValuesFormFieldInput items={field.items} defaultValue={defaultValue} />
        </FormFieldWrapper>
      );

    case FieldType.select:
      return (
        <FormFieldWrapper
          name={String(field.name)}
          title={field.title}
          label={field.label}
          description={field.description}
          required={field.required}
          help={field.help}
          error={error}>
          <GenericSelect
            source={field.source}
            defaultValue={defaultValue}
            multiple={field.multiple}
          />
        </FormFieldWrapper>
      );

    case FieldType.composite:
      return (
        <CompositeFormFieldElement
          name={field.name}
          defaultValue={defaultValue}
          title={field.title}
          fields={field.fields}
          required={field.required}
          errors={errors}
          columnsCount={field.columnsCount}
        />
      );

    case FieldType.embedded:
      return (
        <EmbeddedFormFieldElement
          defaultValue={defaultValue}
          title={field.title}
          fields={field.fields}
          required={field.required}
          errors={errors}
          columnsCount={field.columnsCount}
        />
      );

    case FieldType.list:
      return (
        <ListFormFieldElement
          name={String(field.name)}
          defaultValue={defaultValue}
          title={field.title}
          itemTitle={field.itemTitle}
          label={field.label}
          fields={field.fields}
          required={field.required}
          error={error}
        />
      );

    case FieldType.file:
      return (
        <FormFieldWrapper
          name={String(name)}
          title={title}
          label={field.label}
          description={field.description}
          required={field.required}
          help={field.help}
          error={error}>
          <FileFormFieldElement path={field.path} value={getFileValue(defaultValue)} />
        </FormFieldWrapper>
      );

    case FieldType.date:
      return (
        <FormFieldWrapper
          name={String(name)}
          title={title}
          label={field.label}
          description={field.description}
          required={field.required}
          help={field.help}
          error={error}>
          <DateFormFieldElement defaultValue={defaultValue} />
        </FormFieldWrapper>
      );
  }
}

const getFileValue = (value: unknown): string => {
  if (
    !!value &&
    Array.isArray(value) &&
    !value.some((item: unknown): item is string => typeof item !== 'string')
  ) {
    if (value.length === 0) {
      return '';
    }
    return value[value.length - 1];
  }

  return typeof value === 'string' ? value : '';
};
